import { AxiosError } from 'axios';
import {
    createBrowserRouter, Navigate, Outlet, redirect,
} from 'react-router-dom';

import { authQueries } from './api';
import { PrivateRoute } from './components';
import {
    AppRoute, UserRole,
} from './constants';
import {
    ErrorPageLayout, LoginPageLayout, PageLayout,
} from './layouts';
import {
    ChangelogPage, CommentsPage, ErrorPage, EventsPage, LoginPage,
    MyCommentsPage,
    ObjectionCreatePage,
    ObjectionDetailPage,
    ObjectionEditPage,
    ObjectionsPage,
    ProjectCreatePage,
    ProjectDetailsPage,
    ProjectEditPage,
    ProjectsPage,
    ScriptCreatePage,
    ScriptDetailsPage,
    ScriptEditPage,
    SettingsPage,
    StatisticsPage,
    SupportTicketsPage,
    UserCreatePage,
    UserDetailPage,
    UserEditPage,
    UserProjectsPage,
    UsersPage,
} from './pages';
import { useUserStore } from './stores';
import { queryClient } from './utils';

const initialLoader = async () => {
    try {
        const { data } = await queryClient.fetchQuery(authQueries.checkUser());
        useUserStore.getState().setUser(data);
        return { user: data };
    } catch (error) {
        if (error instanceof AxiosError) {
            const status = error.response?.status;

            if (status === 401) {
                throw redirect(AppRoute.Login);
            }

            if (status && status > 401) {
                throw redirect(`/${status}`);
            }

            if (!error.response) {
                return null;
            }

            throw redirect(AppRoute.Error);
        }

        return null;
    }
};

export const router = createBrowserRouter([
    {
        path: AppRoute.Root,
        loader: initialLoader,
        element: <PageLayout />,
        children: [
            {
                path: AppRoute.Root,
                element: <Navigate to={AppRoute.Projects} />,
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter, UserRole.Manager]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.Projects,
                        element: <ProjectsPage />,
                    },
                    {
                        path: AppRoute.ProjectDetail,
                        element: <ProjectDetailsPage />,
                    },
                ],
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.ProjectCreate,
                        element: <ProjectCreatePage />,
                    },
                ],
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.ProjectEdit,
                        element: <ProjectEditPage />,
                    },
                ],
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter, UserRole.Manager]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.ScriptCreate,
                        element: <ScriptCreatePage />,
                    },
                    {
                        path: AppRoute.ScriptDetail,
                        element: <ScriptDetailsPage />,
                    },
                    {
                        path: AppRoute.ScriptEdit,
                        element: <ScriptEditPage />,
                    },
                ],
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter, UserRole.Manager]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.Objections,
                        element: <ObjectionsPage />,
                    },
                    {
                        path: AppRoute.ObjectionDetail,
                        element: <ObjectionDetailPage />,
                    },
                ],
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.ObjectionCreate,
                        element: <ObjectionCreatePage />,
                    },
                    {
                        path: AppRoute.ObjectionEdit,
                        element: <ObjectionEditPage />,
                    },
                ],
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.Users,
                        element: <UsersPage />,
                    },
                    {
                        path: AppRoute.UserCreate,
                        element: <UserCreatePage />,
                    },
                    {
                        path: AppRoute.UserDetail,
                        element: <UserDetailPage />,
                    },
                    {
                        path: AppRoute.UserEdit,
                        element: <UserEditPage />,
                    },
                ],
            },
            {
                path: AppRoute.UserProjects,
                element: <UserProjectsPage />,
            },
            {
                path: AppRoute.Events,
                element: <EventsPage />,
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.Statistics,
                        element: <StatisticsPage />,
                    },
                ],
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.Settings,
                        element: <SettingsPage />,
                    },
                ],
            },
            {
                path: AppRoute.Changelog,
                element: <ChangelogPage />,
            },
            {
                path: AppRoute.MyComments,
                element: <MyCommentsPage />,
            },
            {
                element: (
                    <PrivateRoute roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                        <Outlet />
                    </PrivateRoute>
                ),
                children: [
                    {
                        path: AppRoute.Comments,
                        element: <CommentsPage />,
                    },
                ],
            },
            // SupportTickets — розділ прихований
            // {
            //     element: (
            //         <PrivateRoute roles={[UserRole.SuperAdmin]}>
            //             <Outlet />
            //         </PrivateRoute>
            //     ),
            //     children: [
            //         {
            //             path: AppRoute.SupportTickets,
            //             element: <SupportTicketsPage />,
            //         },
            //     ],
            // },
        ],
    },
    {
        element: <LoginPageLayout />,
        children: [
            {
                path: AppRoute.Login,
                element: <LoginPage />,
            },
        ],
    },
    {
        element: <ErrorPageLayout />,
        children: [
            {
                path: AppRoute.Forbidden,
                element: <ErrorPage statusCode="403" />,
            },
            {
                path: AppRoute.NotFound,
                element: <ErrorPage statusCode="404" />,
            },
            {
                path: AppRoute.TooManyRequests,
                element: <ErrorPage statusCode="429" />,
            },
            {
                path: AppRoute.InternalServerError,
                element: <ErrorPage statusCode="500" />,
            },
            {
                path: AppRoute.Error,
                element: <ErrorPage />,
            },
            {
                path: AppRoute.Unmatched,
                element: <ErrorPage statusCode="404" />,
            },
        ],
    },
]);
